import { useEffect, useState } from "react";
import type { Game } from "../types/game";
import {
  getGenres,
  PLATFORM_OPTIONS,
  searchGames,
  type GenreOption,
} from "../lib/api";
import GameCard from "../components/GameCard";
import GameDetail from "../components/GameDetail";

type CoopMode = "any" | "online" | "local" | "split";
type Audience = "solo" | "friends";

export default function Recommend() {
  const [genres, setGenres] = useState<GenreOption[]>([]);
  useEffect(() => {
    getGenres().then(setGenres).catch(() => setGenres([]));
  }, []);

  // Answers
  const [platformId, setPlatformId] = useState<string>("");
  const [genreId, setGenreId] = useState<string>("");
  const [audience, setAudience] = useState<Audience>("solo");
  const [coopMode, setCoopMode] = useState<CoopMode>("any");

  // Results state
  const [results, setResults] = useState<Game[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Game | null>(null);

  async function recommend(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResults(null);
    setSelected(null);
    try {
      const tags: string[] = [];
      if (audience === "friends") {
        switch (coopMode) {
          case "online":
            tags.push("online-co-op");
            break;
          case "local":
            tags.push("local-co-op");
            break;
          case "split":
            tags.push("split-screen");
            break;
          case "any":
            tags.push("co-op");
            break;
        }
      }
      const games = await searchGames({
        genreId: genreId || undefined,
        platformId: platformId || undefined,
        tags,
        pageSize: 40,
      });
      setResults(games.slice(0, 9));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResults(null);
    setSelected(null);
    setError(null);
  }

  if (selected) {
    return <GameDetail game={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <section>
      <div className="rec-intro">
        <h1 style={{ marginTop: 0 }}>What should you play next?</h1>
        <p style={{ color: "#b3b9c5", marginTop: 0 }}>
          Answer a few questions and we'll suggest popular games that match.
        </p>

        <form className="rec-form" onSubmit={recommend}>
        <div className="rec-q">
          <div className="rec-q-title">1. What console / device?</div>
          <div className="pills">
            <PillButton
              active={platformId === ""}
              onClick={() => setPlatformId("")}
            >
              Any
            </PillButton>
            {PLATFORM_OPTIONS.map((p) => (
              <PillButton
                key={p.id}
                active={platformId === p.id}
                onClick={() => setPlatformId(p.id)}
              >
                {p.name}
              </PillButton>
            ))}
          </div>
        </div>

        <div className="rec-q">
          <div className="rec-q-title">2. Any genre in mind?</div>
          <select
            value={genreId}
            onChange={(e) => setGenreId(e.target.value)}
            style={{ minWidth: 220 }}
          >
            <option value="">Any genre</option>
            {genres.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        <div className="rec-q">
          <div className="rec-q-title">3. Playing alone or with friends?</div>
          <div className="pills">
            <PillButton
              active={audience === "solo"}
              onClick={() => setAudience("solo")}
            >
              Solo
            </PillButton>
            <PillButton
              active={audience === "friends"}
              onClick={() => setAudience("friends")}
            >
              With friends
            </PillButton>
          </div>
        </div>

        {audience === "friends" && (
          <div className="rec-q">
            <div className="rec-q-title">4. How do you want to play together?</div>
            <div className="pills">
              <PillButton
                active={coopMode === "any"}
                onClick={() => setCoopMode("any")}
              >
                Any co-op
              </PillButton>
              <PillButton
                active={coopMode === "online"}
                onClick={() => setCoopMode("online")}
              >
                Online co-op
              </PillButton>
              <PillButton
                active={coopMode === "local"}
                onClick={() => setCoopMode("local")}
              >
                Local co-op
              </PillButton>
              <PillButton
                active={coopMode === "split"}
                onClick={() => setCoopMode("split")}
              >
                Split-screen
              </PillButton>
            </div>
          </div>
        )}

        <div className="rec-actions">
          <button type="submit" disabled={loading} className="cta">
            {loading ? "Finding games…" : "Recommend me a game"}
          </button>
          {results && (
            <button type="button" onClick={reset}>
              Start over
            </button>
          )}
          </div>
        </form>
      </div>

      {error && <div className="status-error">⚠ {error}</div>}

      {results && (
        <section style={{ marginTop: "2rem" }}>
          <h2 style={{ marginBottom: "0.5rem" }}>Top picks for you</h2>
          <p style={{ color: "#9098a8", marginTop: 0 }}>
            Ranked by popularity within your filters. Click any game for full
            multiplayer details.
          </p>
          {results.length === 0 ? (
            <p style={{ color: "#9098a8" }}>
              No matches. Try loosening a filter (e.g. "Any genre" or "Any co-op").
            </p>
          ) : (
            <div className="grid">
              {results.map((g) => (
                <GameCard key={g.id} game={g} onClick={() => setSelected(g)} />
              ))}
            </div>
          )}
        </section>
      )}
    </section>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pill ${active ? "pill-active" : ""}`}
    >
      {children}
    </button>
  );
}
